// src/Theme/helpers/createThemeHelpers.tsx
import { forwardRef, memo, useMemo } from "react";

// src/Theme/helpers/comparator.ts
function shallowEqual(objA, objB) {
  if (Object.is(objA, objB)) {
    return true;
  }
  if (typeof objA !== "object" || objA === null || typeof objB !== "object" || objB === null) {
    return false;
  }
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (let i = 0; i < keysA.length; i += 1) {
    if (!Object.prototype.hasOwnProperty.call(objB, keysA[i]) || !Object.is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }
  return true;
}
var comparePropsAndTheme = (oldProps, newProps) => {
  const { theme: oldTheme, ...otherOldProps } = oldProps;
  const { theme: newTheme, ...otherNewProps } = newProps;
  return shallowEqual(otherOldProps, otherNewProps) && shallowEqual(oldTheme, newTheme);
};

// src/Theme/helpers/createActiveThemes.ts
import { mergeDeepRight } from "ramda";
function createActiveComponentTheme({
  defaults,
  generatedComponentTheme,
  props,
  themeOverrides
}) {
  if (!Object.prototype.hasOwnProperty.call(generatedComponentTheme, "controls")) {
    return mergeDeepRight(
      generatedComponentTheme.base,
      themeOverrides ?? {}
    );
  }
  const { controls } = generatedComponentTheme;
  return mergeDeepRight(
    mergeDeepRight(
      generatedComponentTheme.base,
      Object.entries(controls ?? {}).reduce((styles, [key, values]) => {
        const propValue = props[key];
        const defaultValue = defaults == null ? void 0 : defaults[key];
        return mergeDeepRight(
          styles,
          values[propValue] ?? values[defaultValue] ?? {}
        );
      }, {})
    ),
    themeOverrides ?? {}
  );
}
function getControlProps({
  defaults,
  generatedComponentTheme,
  props
}) {
  const { controls } = generatedComponentTheme;
  return typeof controls !== "undefined" ? Object.keys(controls).reduce(
    (cP, controlKey) => {
      const normalizedKey = String(
        controlKey
      );
      cP[normalizedKey] = props[normalizedKey] ?? defaults[normalizedKey];
      return cP;
    },
    {}
  ) : void 0;
}

// src/Theme/helpers/createThemeHelpers.tsx
import { jsx } from "react/jsx-runtime";
function createWithTheme({
  useThemeGlobals
}) {
  function withTheme(baseThemeGenerator, controlsThemeGenerator, defaults) {
    return function makeThemedComponent(Component) {
      const componentThemeGenerator = (globals) => controlsThemeGenerator ? {
        base: baseThemeGenerator(globals),
        controls: controlsThemeGenerator == null ? void 0 : controlsThemeGenerator(globals)
      } : {
        base: baseThemeGenerator(globals)
      };
      const ThemedComponent = ({ theme: themeOverrides, ...props }, forwardedRef) => {
        const globals = useThemeGlobals();
        const generatedComponentTheme = useMemo(
          () => componentThemeGenerator(globals),
          [globals]
        );
        const activeComponentTheme = createActiveComponentTheme({
          defaults,
          generatedComponentTheme,
          props,
          themeOverrides
        });
        const controlProps = getControlProps({
          props,
          defaults,
          generatedComponentTheme
        });
        const themedComponent = /* @__PURE__ */ jsx(Component, {
          ...props,
          ...controlProps,
          theme: activeComponentTheme,
          ref: forwardedRef
        });
        return themedComponent;
      };
      const ThemedComponentWithForwardRef = forwardRef(ThemedComponent);
      const ThemedComponentWithMemo = memo(
        ThemedComponentWithForwardRef,
        comparePropsAndTheme
      );
      return ThemedComponentWithMemo;
    };
  }
  return withTheme;
}
function createThemeHelpers(useThemeGlobals) {
  return {
    withTheme: createWithTheme({ useThemeGlobals })
  };
}

// src/context/I18n.tsx
import { createContext } from "react";
var I18nContext = createContext({
  locale: "en-US",
  languageCode: "en",
  countryCode: "US"
});
export {
  I18nContext,
  createThemeHelpers
};
